import { Transition } from "@headlessui/react"

import { FormField, FormFields } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import type { User } from "@/types"

interface UserFormData {
  user: {
    name: string
    avatar: File | null
  }
}

interface UserFormProps {
  user: User
  onSubmit: (e: React.FormEvent) => void
  data: UserFormData
  setData: (key: "user", value: UserFormData["user"]) => void
  processing: boolean
  errors: Record<string, unknown>
  recentlySuccessful: boolean
  preview?: string | null
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export default function UserForm({
  user,
  onSubmit,
  data,
  setData,
  processing,
  errors,
  recentlySuccessful,
  preview,
  onFileChange,
}: UserFormProps) {
  // Rails returns nested error objects; Inertia's types only know flat keys.
  const userErrors = errors.user as
    | { name?: string; avatar?: string }
    | undefined

  return (
    <form onSubmit={onSubmit}>
      <FormFields>
        <FormField
          label="Name"
          htmlFor="name"
          error={userErrors?.name ?? undefined}
        >
          <Input
            id="name"
            name="name"
            value={data.user.name}
            onChange={(e) =>
              setData("user", { ...data.user, name: e.target.value })
            }
            autoComplete="name"
            placeholder="Full name"
          />
        </FormField>

        <ImageUpload
          label="Photo"
          id="avatar"
          preview={preview ?? null}
          currentUrl={user.avatar_url}
          onFileChange={onFileChange}
          error={userErrors?.avatar ?? undefined}
          buttonLabel="Upload photo"
        />

        <div className="flex items-center gap-4">
          <Button disabled={processing}>Save</Button>

          <Transition
            show={recentlySuccessful}
            enter="transition ease-in-out"
            enterFrom="opacity-0"
            leave="transition ease-in-out"
            leaveTo="opacity-0"
          >
            <p className="text-sm text-neutral-600">Saved</p>
          </Transition>
        </div>
      </FormFields>
    </form>
  )
}
